/**
 * Named use-case presets for the Remove object operation.
 *
 * The backend has always accepted `params.preset` (`auto` | `general` |
 * `ghost_mannequin`, see `filespin/lib/imaging/fal_edit.py`) — it selects the
 * prompt template. Until now the dashboard sent nothing and let the server
 * infer one from the detected subject via its `is_apparel` classifier, so the
 * flagship retail case (ghost mannequin) worked only when that guess landed and
 * was named nowhere a customer could see it.
 *
 * A preset here is three things: the server template, the background the
 * template needs, and the vocabulary of props worth removing for that use case.
 * Deliberately hook-free data plus one pure function, so the gate matrix is
 * unit-testable and this is the natural thing to replace with a server-driven
 * manifest later (same shape as `src/chat/nudges/promptCatalog.ts`).
 */

import type messages from '../messages';
/**
 * The generative remove_object params.
 *
 * Moved here when the standalone Remove object form was retired: the shape is
 * the operation's contract (see `filespin/lib/imaging/fal_edit.py`), not a
 * component's props. `preset` holds one of OUR ids and is translated to the
 * server's template name on the way out.
 */
export type RemoveObjectParamsValue = {
  /** Primary object to keep. */
  subject: string;
  /** Objects to remove. Wire values matched against the server's vocabulary. */
  remove: string[];
  /** 'transparent' | 'white' | 'light_grey' | 'studio_grey' | 'original' | #hex */
  background: string;
  refinePrompt: string;
  /** RemoveObjectPresetId | undefined (= Auto) */
  preset?: string;
};

/** Our own ids. Two presets share a server template and differ by background,
 *  so these cannot just be the server's values. */
export type RemoveObjectPresetId = 'ghost_mannequin' | 'isolate_white' | 'erase_people';

/** What the server's `validate_object_removal_params` accepts. `auto` is the
 *  server-side default and is expressed here by sending no preset at all. */
export type ServerPreset = 'general' | 'ghost_mannequin';

export type RemoveObjectPreset = {
  id: RemoveObjectPresetId;
  /** Prompt template requested from the server. */
  serverPreset: ServerPreset;
  /** Message keys, resolved by the component — keeps this module intl-free. */
  labelMsg: keyof typeof messages;
  hintMsg: keyof typeof messages;
  /** The background this template is built around. */
  background: string;
  /** Prop vocabulary added to (never substituted for) the detected list. */
  addRemove: string[];
  /**
   * `background: 'original'` is meaningless for this preset. Ghost mannequin is
   * the case: `compose_prompt` forces a flat background and silently rewrites
   * `original` to white, so offering the choice would be a lie.
   */
  disallowOriginal?: boolean;
};

export const REMOVE_OBJECT_PRESETS: RemoveObjectPreset[] = [
  {
    id: 'ghost_mannequin',
    serverPreset: 'ghost_mannequin',
    labelMsg: 'aiRemovePresetGhost',
    hintMsg: 'aiRemovePresetGhostHint',
    // Transparent by default (the extra matting call is absorbed in the same
    // 8 credits): the cut-out garment drops into any layout, and a customer
    // who wants the catalog look picks White in the dialog's Background row.
    // (Reversed 2026-08-22 — this originally defaulted to white.)
    background: 'transparent',
    // The server's own ghost default is mannequin/string/clips; `hanger` is in
    // its known vocabulary and is just as common in supplier photography.
    addRemove: ['mannequin', 'hanger', 'string', 'clips'],
    disallowOriginal: true,
  },
  {
    id: 'isolate_white',
    serverPreset: 'general',
    labelMsg: 'aiRemovePresetIsolate',
    hintMsg: 'aiRemovePresetIsolateHint',
    background: 'white',
    // Nothing: the general isolate template already removes "the background and
    // surrounding clutter" on its own, and naming props here would invent
    // objects that aren't in the photo.
    addRemove: [],
  },
  {
    id: 'erase_people',
    serverPreset: 'general',
    labelMsg: 'aiRemovePresetErasePeople',
    hintMsg: 'aiRemovePresetErasePeopleHint',
    // Keeping the original background is what makes this in-scene erasure
    // rather than isolation — the server picks its in-scene template off this.
    background: 'original',
    addRemove: ['background people', 'other people'],
  },
];

export const getRemoveObjectPreset = (
  id?: string,
): RemoveObjectPreset | undefined => REMOVE_OBJECT_PRESETS.find((p) => p.id === id);

/**
 * The value for `params.preset`. `undefined` for Auto — we send no key at all
 * rather than a literal `"auto"`, so the request stays byte-identical to what
 * the dashboard sent before presets existed.
 */
export const serverPresetFor = (id?: string): ServerPreset | undefined =>
  getRemoveObjectPreset(id)?.serverPreset;

const has = (list: string[], token: string) =>
  list.some((t) => t.toLowerCase() === token.toLowerCase());

/** Case-insensitive append, preserving existing order and casing. */
const union = (current: string[], additions: string[]): string[] => {
  const out = [...current];
  additions.forEach((token) => {
    if (!has(out, token)) out.push(token);
  });
  return out;
};

/**
 * Apply a preset to the current form values.
 *
 * Its vocabulary is added to the remove list rather than replacing it:
 * `GET /edit/detect` has already prefilled the real nouns for *this* photo (a
 * dress hanging on a door yields `door, wall`), and discarding that is worse
 * than the generic guess it would be replaced with. `subject` and
 * `refinePrompt` are never touched — they are per-image truth the user or the
 * detector supplied.
 *
 * Switching presets retires the outgoing preset's vocabulary first. Without
 * that, toggling between presets accumulates every token, so a ghost-mannequin
 * prompt ends up asking the model to remove "background people" that are not in
 * the photo. The cost is narrow and visible: a token the detector happened to
 * find AND the outgoing preset also contributes is dropped on the switch, and
 * the user can see the chips and re-add it.
 *
 * Passing `undefined` selects Auto: it clears the preset and leaves every other
 * field exactly as the user left it, since Auto makes no claim about them.
 */
export const applyRemoveObjectPreset = (
  value: RemoveObjectParamsValue,
  id?: RemoveObjectPresetId,
): RemoveObjectParamsValue => {
  const preset = getRemoveObjectPreset(id);
  if (!preset) return { ...value, preset: undefined };

  const outgoing = value.preset === preset.id ? undefined : getRemoveObjectPreset(value.preset);
  const retained = (value.remove || []).filter(
    (token) => !outgoing || !has(outgoing.addRemove, token) || has(preset.addRemove, token),
  );

  return {
    ...value,
    preset: preset.id,
    background: preset.background,
    remove: union(retained, preset.addRemove),
  };
};
